import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  EmptyStateModule,
  ModalComponent,
  ModalModule,
  ModalResult,
  TableComponent,
  TableConfig,
  TableModule
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedModule } from '../../../../shared/shared.module';
import { CasingPurchaseDetails } from '../../models/casing-purchase.model';
import { CreateCasingPurchase } from '../../models/create-casing-purchase.model';
import { MatTableDataSource } from '@angular/material/table';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';

@Component({
  selector: 'app-casing-purchase-create',
  templateUrl: './casing-purchase-create.component.html',
  styleUrl: './casing-purchase-create.component.scss',
  imports: [SharedModule, TableModule, EmptyStateModule, ModalModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingPurchaseCreateComponent implements OnInit {
  casingPurchaseDetailsFormGroup: FormGroup;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingPurchaseDetails>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  public route = inject(ActivatedRoute);

  casingPurchaseDetails: CasingPurchaseDetails[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: any;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  productSaveDisabled = true;
  saveEnabled = false;
  isCreateInProgress = false;

  // Copy mode properties
  isCopyMode = false;
  casingPurchasePk: string | null = null;
  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  sizeList: string[] = [];
  descriptionList: string[] = [];

  // Initialize Data source for table
  dataSource: MatTableDataSource<CasingPurchaseDetails>;

  tableConfig: TableConfig<any> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {
      size: { cell: (row) => row.size, header: this.translationService.translate('sizeText') },
      sizeDescription: { cell: (row) => row.sizeDescription, header: this.translationService.translate('descriptionText') },
      purchasePrice: { cell: 'purchasePrice', header: this.translationService.translate('purchasePriceText') }
    }
  };

  constructor() {
    this.casingPurchaseDetailsFormGroup = this.fb.group({
      code: '',
      description: ''
    });
    this.filterForm = this.fb.group({
      size: [[]],
      description: [[]],
      purchasePrice: [[]]
    });
    this.filterFormBackend = this.fb.group({
      size: [[]],
      description: [[]],
      purchasePrice: [[]]
    });
  }

  ngOnInit(): void {
    // Detect copy mode from route param
    this.casingPurchasePk = this.route.snapshot.paramMap.get('casingPurchasePk');
    this.isCopyMode = !!this.casingPurchasePk;

    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }

    if (this.isCopyMode && this.casingPurchasePk) {
      // Copy mode: fetch ALL data upfront so every row is available for display and save
      this.loadCopyModeData();
    } else {
      // Create mode: original behavior - load all data
      this.productDataService.getCasingPurchaseDetailsForCreateCasingPurchase().subscribe((response: any) => {
        this.casingPurchaseDetails = response.data || [];
        this.updateDisplayedRowsForm(this.casingPurchaseDetails);
        this.dataSource = new MatTableDataSource(this.casingPurchaseDetails);
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Loads all casing purchase details for copy mode in a single request.
   * Uses a large pageSize to fetch all rows at once so they are all
   * available in the table (with client-side pagination) and for saving.
   */
  private loadCopyModeData(): void {
    this.isCreateInProgress = true;
    this.cdr.markForCheck();

    const ALL_ROWS_PAGE_SIZE = 10000;
    this.productDataService.getCasingPurchaseDetails({}, this.casingPurchasePk, 1, ALL_ROWS_PAGE_SIZE).subscribe({
      next: (response: any) => {
        this.casingPurchaseDetails = response.data.casingPurchaseDetails || [];
        this.updateDisplayedRowsForm(this.casingPurchaseDetails);
        this.dataSource = new MatTableDataSource(this.casingPurchaseDetails);
        this.isCreateInProgress = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isCreateInProgress = false;
        this.cdr.markForCheck();
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  createRowForm(row: CasingPurchaseDetails): FormGroup {
    return this.fb.group({
      size: [row.size],
      sizeDescription: [row.sizeDescription],
      purchasePrice: [row.purchasePrice]
    });
  }

  updateDisplayedRowsForm(rows: any[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  getControl(item: CasingPurchaseDetails, controlName: string): FormControl {
    const index = this.casingPurchaseDetails.findIndex((row) => row.casingSizeFk === item.casingSizeFk);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  cancel(): void {
    this.router.navigate(['/home/productData/casingPurchase']);
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    const codeControl = this.casingPurchaseDetailsFormGroup.get('code');
    const noCodeInlineErrors = codeControl.valid && !codeControl.errors;
    const descriptionControl = this.casingPurchaseDetailsFormGroup.get('description');
    const noDescriptionInlineErrors = descriptionControl.valid && !descriptionControl.errors;

    if (!codeControl.value) {
      codeControl.markAsTouched();
      codeControl.setErrors({ required: true });
      this.cdr.markForCheck();
    }

    if (!descriptionControl.value) {
      descriptionControl.markAsTouched();
      descriptionControl.setErrors({ required: true });
      this.cdr.markForCheck();
    }
    const code = this.casingPurchaseDetailsFormGroup.get('code').value;
    const description = this.casingPurchaseDetailsFormGroup.get('description').value;
    if (!code || !description || !noCodeInlineErrors || !noDescriptionInlineErrors) {
      return;
    }

    this.isCreateInProgress = true;
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.casingPurchaseDetails.length; i++) {
      const original = this.casingPurchaseDetails[i];
      const current = formArray.at(i).value;
      const normalizedPurchasePrice = current.purchasePrice === null || current.purchasePrice === '' ? 0 : current.purchasePrice;
      modifiedRows.push({
        ...current,
        purchasePrice: normalizedPurchasePrice,
        casingPurchaseDetailId: original.casingPurchaseDetailId,
        casingSizeFk: original.casingSizeFk
      });
    }
    const payload: CreateCasingPurchase = {
      code: this.casingPurchaseDetailsFormGroup.get('code').value,
      description: this.casingPurchaseDetailsFormGroup.get('description').value,
      casingPurchaseDetails: modifiedRows
    };
    this.productDataService.createCasingPurchase(payload).subscribe({
      next: () => {
        this.isCreateInProgress = false;
        setTimeout(() => {
          this.router.navigate(['/home/productData/casingPurchase']);
        }, 2000);
        this.cdr.markForCheck();
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('camSaveSuccessText')
        } as Alert);
      },
      error: () => {
        this.isCreateInProgress = false;
        this.cdr.markForCheck();
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  checkIfCasingPurchaseCodeExists() {
    const casingPurchaseCode = this.casingPurchaseDetailsFormGroup.get('code').value;
    this.productDataService.casingPurchaseCodeExists(casingPurchaseCode).subscribe((exists) => {
      if (exists) {
        this.casingPurchaseDetailsFormGroup.get('code').setErrors({ exists: true });
      } else {
        this.casingPurchaseDetailsFormGroup.get('code').setErrors(null);
      }
    });
  }

  /**
   * Called on keydown to mark deletion intent for environments where inputType is not available.
   */
  handleKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      input.dataset.isDeleting = 'true';
    }
  }

  /**
   * Restrict input to at most two digits after decimal point.
   * Allows empty string so user can delete all characters (no forced '0').
   * Preserves caret position and keeps FormControl in sync.
   */
  restrictTwoDecimalPlaces(event: Event, item: CasingPurchaseDetails): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const inputEventType = (event as any)?.inputType ?? '';
    const isDeleting = input.dataset.isDeleting === 'true' || inputEventType.startsWith('delete');

    const originalValue = input.value ?? '';
    const selectionStart = input.selectionStart ?? originalValue.length;

    const cleanValue = (val: string) => {
      if (val === '') {
        return '';
      }
      let v = val.replace(/[^0-9.]/g, '');
      v = v.replace(/(\..*)\./g, '$1');
      if (v === '.') {
        return '0.';
      }
      if (v.startsWith('.')) {
        v = '0' + v;
      }
      if (v.includes('.')) {
        const [intPart, fracPart] = v.split('.');
        v = intPart + '.' + (fracPart ? fracPart.slice(0, 2) : '');
      } else {
        v = v.replace(/^0+(\d)/, '$1');
      }
      return v;
    };

    let newValue = cleanValue(originalValue);

    if (isDeleting && newValue === '0') {
      newValue = '';
    }

    const originalPrefix = originalValue.slice(0, selectionStart);
    let cleanedPrefix = cleanValue(originalPrefix);
    if (isDeleting && cleanedPrefix === '0') {
      cleanedPrefix = '';
    }

    if (input.dataset.isDeleting) {
      delete input.dataset.isDeleting;
    }

    if (newValue === originalValue) {
      try {
        const control = this.getControl(item, 'purchasePrice');
        if (control && control.value !== (newValue === '' ? null : newValue)) {
          control.setValue(newValue === '' ? null : newValue, { emitEvent: false });
        }
      } catch {
        // ignore
      }
      return;
    }

    const newCaretPos = Math.min(cleanedPrefix.length, newValue.length);
    input.value = newValue;
    try {
      input.setSelectionRange(newCaretPos, newCaretPos);
    } catch {
      // ignore
    }

    try {
      const control = this.getControl(item, 'purchasePrice');
      if (control) {
        control.setValue(newValue === '' ? null : newValue, { emitEvent: false });
      }
    } catch {
      // ignore
    }

    this.cdr.markForCheck();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.saveEnabled) {
      $event.returnValue = true;
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.updateDisplayedRowsForm(this.casingPurchaseDetails);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters({}, true);
      }
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.saveEnabled) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      size: this.filterForm.get('size')?.value || '',
      description: this.filterForm.get('description')?.value || '',
      purchasePrice: this.filterForm.get('purchasePrice')?.value || ''
    });
  }

  clearFilters(): void {
    this.filterForm.reset();
  }
}
